import React, { useMemo, useState } from "react";
import { connect } from "react-redux";
import { useTranslation } from "react-i18next";

import {
  SPACE,
  messages,
  EMPTY_STRING,
  discountStatus,
} from "../../../../constants";
import { ReactComponent as InfoIcon } from "../../../../assets/icons/info.svg";
import { isArrayWithItems, numberToDecimals } from "../../../../shared";
import IconButton from "../../../../shared/components/IconButton/IconButton";
import CustomTooltip from "../../../../shared/components/ToolTip/CustomTooltip";
import { eventBookingActions } from "../../../../redux/actions";

const CheckoutRow = ({
  label,
  value,
  isStrong = false,
  className = EMPTY_STRING,
}) => {
  return (
    <div
      className={`d-flex justify-content-between align-items-center ${className}`}
    >
      <p className="label-content-600 mt-3 mb-3">{label}</p>
      <p
        className={`${
          isStrong ? "label-content-600" : "text-content"
        } mt-3 mb-3 checkout-amount`}
      >
        {value}
      </p>
    </div>
  );
};

const Checkout = (props) => {
  const {
    updatedFormState,
    totalFee,
    totalTip,
    serviceFee,
    taxPercentValue,
    checkboxes,
    handleCheckboxChange,
    setIsModalOpen,
    promoCodeDiscount,
    setPromoCodeDiscount,
    isGiftCardChecked,
    setIsGiftCardChecked,
    giftCardDiscount,
    setGiftCardDiscount,
    discount,
    remainingBalance,
    alreadyPaidAmount,
    showDiscountButton,
    totalFeeBackupRef,
    initialTotalFeeRef,
    giftCardDiscountApplied,
    updatedTotal,
    resetGiftCard,
    editEventOrderDetails,
  } = props;

  const { t } = useTranslation("common");

  const [errors, setErrors] = useState({
    termsAndConditions: EMPTY_STRING,
    privacyPolicy: EMPTY_STRING,
  });

  // Total Amount before applying gift card
  const totalAmount = useMemo(
    () => totalFee + serviceFee + taxPercentValue + totalTip,
    [totalFee, serviceFee, taxPercentValue, totalTip],
  );

  // handle the discount and
  const handlePromoCodeSubmit = (e, status) => {
    e.preventDefault();
    if (props.isAuthenticated) {
      const formData = new FormData(e.target);
      const formProps = Object.fromEntries(formData);
      const dateToSend = {
        code:
          status === discountStatus.PROMO_CODE
            ? formProps.promoCode
            : formProps.giftCard,
        userId: props.userId,
        codeType: status,
      };

      props.applyDiscount(dateToSend, onApplyDiscountSuccess);
    }
  };

  const onApplyDiscountSuccess = (data, codeType) => {
    if (codeType === discountStatus.GIFT_CARD) {
      setGiftCardDiscount(data || null);
    } else if (codeType === discountStatus.PROMO_CODE) {
      setPromoCodeDiscount(data || null);
      initialTotalFeeRef.current =
        totalFeeBackupRef.current - alreadyPaidAmount;
      resetGiftCard();
    }
  };

  const handleCheckbox = (key) => {
    handleCheckboxChange(key);

    const updated = { ...checkboxes, [key]: !checkboxes[key] };
    if (updated[key]) {
      setErrors((prev) => ({ ...prev, [key]: EMPTY_STRING }));
    }
  };

  const validateCheckboxes = () => {
    let newErrors = {
      termsAndConditions: EMPTY_STRING,
      privacyPolicy: EMPTY_STRING,
    };
    let isValid = true;

    if (!checkboxes.termsAndConditions) {
      newErrors.termsAndConditions = messages.ACCEPT_TERMS_AND_CONDITION;
      isValid = false;
    }

    if (!checkboxes.privacyPolicy) {
      newErrors.privacyPolicy = messages.ACCEPT_PRIVACY_POLICY;
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };
  const [isPromoCodeValid, setIsPromoCodeValid] = useState(null);
  const [isGiftCardCodeValid, setIsGiftCardCodeValid] = useState(null);

  const handleCheckoutSubmit = (e) => {
    e.preventDefault();
    if (!validateCheckboxes()) return;
    setIsModalOpen(true);
    props.handleCheckout();
  };

  const handleGiftCardSubmit = (e) => {
    e.preventDefault();

    const input = e.target.giftCard;

    if (!input.value.trim()) {
      setIsGiftCardCodeValid(messages.REQUIRED_FIELD);
      return;
    }

    setIsModalOpen(true);
    handlePromoCodeSubmit(e, discountStatus.GIFT_CARD);
  };

  const shouldShowGiftCardSection = useMemo(() => {
    return (
      !props.isSBTipExtendCart &&
      showDiscountButton &&
      updatedTotal >= 0 &&
      isGiftCardChecked &&
      totalAmount > 0
    );
  }, [
    props.isSBTipExtendCart,
    showDiscountButton,
    updatedTotal,
    isGiftCardChecked,
    totalAmount,
  ]);

  return (
    <div className="checkout-container pl-lg-4 pl-0 mt-lg-5 mt-0 pt-2">
      {/* Promo Code */}
      {!props.isSBTipExtendCart && showDiscountButton && totalFee >= 0 && (
        <>
          <h2 className="cart-summary-title">{t("apply_coupon_code")}</h2>
          <form
            onSubmit={(e) => {
              e.preventDefault();

              const input = e.target.promoCode;

              if (!input.value.trim()) {
                setIsPromoCodeValid("This field is required");
                return;
              }

              setIsModalOpen(true);
              handlePromoCodeSubmit(e, discountStatus.PROMO_CODE);
            }}
            autoComplete="off"
          >
            <div
              className={`d-flex align-items-center ${
                isPromoCodeValid ? "mb-1" : "mb-3"
              }`}
            >
              <input
                id="form1"
                name="promoCode"
                type="text"
                maxLength={35}
                className={`form-control coupon-code text-content ${
                  isPromoCodeValid ? "is-invalid" : EMPTY_STRING
                }`}
                placeholder="enter promo code"
                onInput={() => setIsPromoCodeValid(null)}
              />
              <button className="button-common button-fill checkout-apply-btn">
                {messages.APPLY}
              </button>
            </div>
            {isPromoCodeValid && (
              <p className="invalid-field-error m-0">{isPromoCodeValid}</p>
            )}
          </form>
        </>
      )}

      {/* Gift Card */}
      {!props.isSBTipExtendCart && (
        <div
          className={`d-flex align-items-center pt-1 ${totalAmount < 1 ? "gift-cards-disabled" : "gift-cards"}`}
        >
          <input
            type="checkbox"
            name="giftCard"
            checked={isGiftCardChecked}
            disabled={totalAmount < 1}
            onChange={(e) => {
              setIsGiftCardChecked(e.target.checked);
              if (!e.target.checked) {
                setGiftCardDiscount(null);
              }
            }}
            className="custom-checkout gift-card-checkbox"
          />
          <p className="label-content fw-500 mb-0 ml-2 gift-card-checkbox-text">
            {messages.PAY_WITH_SHUTTERB_CREDITS}
          </p>
        </div>
      )}

      {/* Gift Card Form */}
      {shouldShowGiftCardSection && (
        <form onSubmit={handleGiftCardSubmit} autoComplete="off">
          <div className="d-flex align-items-center mt-3 mb-1">
            <input
              id="form2"
              name="giftCard"
              type="text"
              maxLength={35}
              className={`form-control coupon-code text-content ${
                isGiftCardCodeValid ? "is-invalid" : EMPTY_STRING
              }`}
              placeholder="enter shutterb credits code"
              onInput={() => setIsGiftCardCodeValid(null)}
            />
            <button className="button-common button-fill checkout-apply-btn">
              {messages.APPLY}
            </button>
          </div>
          {isGiftCardCodeValid && (
            <p className="invalid-field-error m-0">{isGiftCardCodeValid}</p>
          )}
        </form>
      )}

      {totalFee > 0 &&
        Array.isArray(editEventOrderDetails?.orderDiscountDetails) &&
        editEventOrderDetails.orderDiscountDetails.filter(
          (discount) => !discount.isGiftCard,
        ).length > 0 && (
          <div className="checkout-discount-list">
            <p className="mb-0 discounts">
              promo code {SPACE}
              <span className="discount-code">
                {editEventOrderDetails?.orderDiscountDetails
                  .filter((discount) => !discount.isGiftCard)
                  .map((discount) => discount.code)
                  .join(", ")}
              </span>
              {SPACE}
              applied previously.
            </p>
          </div>
        )}

      <div>
        {discount > 0 && (
          <CheckoutRow
            label={messages.DISCOUNT}
            value={`$${numberToDecimals(discount)}`}
            className="checkout-row"
          />
        )}

        {promoCodeDiscount?.isGiftCard && (
          <CheckoutRow
            label={messages.REMAINING_BALANCE}
            value={`$${numberToDecimals(remainingBalance)}`}
            className="checkout-row"
          />
        )}

        <CheckoutRow
          label={messages.FEE}
          value={`$${numberToDecimals(totalFee + totalTip)}`}
          className={`checkout-row ${
            totalAmount < 0 ? "balance-due" : EMPTY_STRING
          }`}
        />

        <CheckoutRow
          label={`${messages.SERVICE_FEE}${
            props.miscAmounts?.serviceFee != null
              ? ` (${numberToDecimals(props.miscAmounts.serviceFee)}%)`
              : EMPTY_STRING
          }`}
          value={`$${numberToDecimals(serviceFee)}`}
          className={`checkout-row ${
            totalAmount < 0 ? "balance-due" : EMPTY_STRING
          }`}
        />

        <CheckoutRow
          label={messages.SUB_TOTAL}
          value={`$${numberToDecimals(totalFee + serviceFee + totalTip)}`}
          className={`checkout-row ${
            totalAmount < 0 ? "balance-due" : EMPTY_STRING
          }`}
        />

        <CheckoutRow
          label={`${messages.TAX}${
            props.miscAmounts?.tax != null
              ? ` (${numberToDecimals(props.miscAmounts.tax)}%)`
              : EMPTY_STRING
          }`}
          value={`$${numberToDecimals(taxPercentValue)}`}
          className={`checkout-row ${
            totalAmount < 0 ? "balance-due" : EMPTY_STRING
          }`}
        />

        <CheckoutRow
          label={messages.TOTAL}
          value={`$${numberToDecimals(totalAmount)}`}
          isStrong
          className={`checkout-row ${
            totalAmount < 0 ? "balance-due" : EMPTY_STRING
          }`}
        />

        {shouldShowGiftCardSection && giftCardDiscount && (
          <div className="gift-card-discount-summary">
            <div
              className={`d-flex justify-content-between align-items-center already-paid mb-2`}
            >
              <p className="label-content-600 mb-0">
                {messages.APPLIED_SHUTTERB_CREDITS}
              </p>
              <p className="label-content-600 mb-0">{`$${numberToDecimals(giftCardDiscountApplied)}`}</p>
            </div>

            <div
              className={`d-flex justify-content-between align-items-center update-total`}
            >
              <p className="label-content-600 mb-0">
                {messages.SHUTTERB_CREDITS_BALANCE}
              </p>
              <p className="label-content-600 mb-0">
                {`$${numberToDecimals(remainingBalance)}`}
              </p>
            </div>
          </div>
        )}
      </div>
      <form onSubmit={handleCheckoutSubmit} autoComplete="off">
        <div>
          <div className="d-flex align-items-start checkout-terms">
            <input
              type="checkbox"
              name="termsAndConditions"
              checked={checkboxes.termsAndConditions}
              onChange={() => handleCheckbox("termsAndConditions")}
              className="custom-checkout checkout-checkbox"
            />
            <p className="text-content mb-0 ml-2">
              you have read &amp; accepted{" "}
              <a
                href={`${process.env.REACT_APP_MARKETING_SITE_BASE_URL}/terms-and-conditions/`}
                target="_blank"
                without
                rel="noopener noreferrer"
              >
                {t("terms_and_condition")}
              </a>
            </p>
          </div>

          {errors.termsAndConditions && (
            <p className="invalid-field-error mb-0 mt-1">
              {errors.termsAndConditions}
            </p>
          )}

          <div className="d-flex align-items-start checkout-terms">
            <input
              type="checkbox"
              name="privacyPolicy"
              checked={checkboxes.privacyPolicy}
              onChange={() => handleCheckbox("privacyPolicy")}
              className="custom-checkout checkout-checkbox"
            />
            <p className="text-content mb-0 ml-2">
              you have read &amp; accepted our{" "}
              <a
                href={`${process.env.REACT_APP_MARKETING_SITE_BASE_URL}/privacy-policy/`}
                target="_blank"
                without
                rel="noopener noreferrer"
              >
                {t("privacy_policy")}
              </a>
            </p>
          </div>

          {errors.privacyPolicy && (
            <p className="invalid-field-error mb-0 mt-1">
              {errors.privacyPolicy}
            </p>
          )}
          {!props.isSBTipExtendCart && (
            <>
              <div className="d-flex align-items-start checkout-terms">
                <input
                  type="checkbox"
                  name="isConsentForMarketing"
                  checked={checkboxes.isConsentForMarketing}
                  onChange={() => handleCheckboxChange("isConsentForMarketing")}
                  className="custom-checkout checkout-checkbox"
                />
                <p className="text-content mb-0 ml-2">
                  you grant shutterb permission to feature your content on our
                  social channels, website, and in marketing materials.{" "}
                  <CustomTooltip
                    id={`tooltip1`}
                    title={t("shutterb_permission")}
                    arrow
                    placement="top"
                  >
                    <span tabIndex="0">
                      <IconButton
                        icon={<InfoIcon />}
                        width="12px"
                        height="12px"
                      />
                    </span>
                  </CustomTooltip>
                </p>
              </div>

              <div className="d-flex align-items-start checkout-terms">
                <input
                  type="checkbox"
                  name="isGallerySharedPermission"
                  checked={checkboxes.isGallerySharedPermission}
                  onChange={() =>
                    handleCheckboxChange("isGallerySharedPermission")
                  }
                  className="custom-checkout checkout-checkbox"
                />
                <p className="text-content mb-0 ml-2">
                  shutterb can give access to the event gallery directly to my
                  guests so that they can enjoy and share the content as well!
                </p>
              </div>
            </>
          )}
        </div>

        {!isGiftCardChecked && (
          <button
            type="submit"
            disabled={
              props.loadingButton || !isArrayWithItems(updatedFormState)
            }
            className="button-common button-fill checkout-btn mt-3"
          >
            {totalAmount <= 0
              ? messages.CONFIRM_BOOKING
              : `${messages.PROCEED_TO_CHECKOUT} ($${numberToDecimals(
                  totalAmount,
                )})`}
          </button>
        )}
      </form>

      {shouldShowGiftCardSection && (
        <button
          type="button"
          disabled={props.loadingButton || !isArrayWithItems(updatedFormState)}
          className="button-common button-fill checkout-btn mt-3"
          onClick={handleCheckoutSubmit}
        >
          {messages.PROCEED_TO_CHECKOUT}
          {updatedTotal > 0
            ? ` ($${numberToDecimals(updatedTotal)})`
            : EMPTY_STRING}
        </button>
      )}
    </div>
  );
};

// Mapping the component's props to the reducer's state
const mapStateToProps = (state) => ({
  userId: state.authentication.userId,
  loadingButton: state.system.loadingButton,
  isEditEvent: state.eventBooking.isEditEvent,
  isAuthenticated: state.authentication.isAuthenticated,
  isSBTipExtendCart: state.eventBooking.isSBTipExtendCart,
});

// Mapping the component's props to the related actions
const mapDispatchToProps = (dispatch) => ({
  applyDiscount: (dateToSend, onResponseCallback) =>
    dispatch(eventBookingActions.applyDiscount(dateToSend, onResponseCallback)),
});

// mapping action and store the function via props
export default connect(mapStateToProps, mapDispatchToProps)(Checkout);
